/**
 * crowd_instancer — REAL motion vectors for the count-packed versona crowd
 * (2026-09-25, visual-realism R3: a TAAU precondition).
 *
 * WHY. `CrowdInstancer` (lib/instancing.ts) packs the visible crowd into the
 * front of one InstancedMesh every frame, so slot v holds a DIFFERENT versona
 * from one frame to the next and three's previous-instance-matrix copy (keyed
 * by slot) would hand each body another body's last position — which is why
 * the crowd was pinned static (`pinStaticMotionVectors`: zero object velocity).
 * Pinned, a gliding twin reads as standing still: the temporal resolve drags
 * its history behind it (a ghost trail) and motion blur never sees it walk.
 *
 * HOW. Each LOGICAL instance's ABSOLUTE position is remembered across one
 * frame (logical index = the crowd's stable per-frame iteration order), and at
 * pack time slot v receives Δ = P_prev − P_now of the versona it now holds.
 * The material's positionNode (applied after three's instancing, so
 * `positionLocal` is the instanced vertex) assigns
 *     positionPrevious = positionLocal + Δ[instanceIndex]
 * — the brief's `positionGeometry·R + T_prev` with the CURRENT yaw (a turn in
 * one frame is sub-pixel at crowd range). Δ is absolute-minus-absolute, so the
 * FP64 origin rebase cancels out (three's previous model matrix carries the
 * anchor). A jump over TELEPORT_M (a respawn, a slot reshuffle when a versona
 * joins or leaves the roster) reads as no motion rather than a one-frame streak.
 *
 * ZERO NEW VERTEX BUFFERS: Δ lives in a UNIFORM array indexed by instanceIndex
 * (16 B a slot: 16 KB for a 1,024 crowd, 64 KB — the WebGPU limit — for the
 * 4,096 capsule crowd), uploaded as the packed prefix only. WebGPU only: the
 * velocity target exists only there (TAAU), and WebGL2's 16 KB uniform-block
 * limit would not hold the capsule crowd — the WebGL2 crowd keeps the pin.
 */
import * as THREE from 'three/webgpu';
import { Fn, buffer, instanceIndex, positionLocal, positionPrevious } from 'three/tsl';

/** A per-frame move longer than this (m) is a teleport / reshuffle, not motion. */
export const TELEPORT_M = 3;

let backendSupportsMotion = false;
/** Set once the renderer exists (AssetModule.init): WebGPU backends get real crowd velocity. */
export function setCrowdMotionBackend(isWebGPU: boolean): void { backendSupportsMotion = isWebGPU; }
export function crowdMotionSupported(): boolean {
  if ((globalThis as { __crowdMotionOff?: boolean }).__crowdMotionOff === true) return false; // probe lever: the pinned (before) crowd
  return backendSupportsMotion;
}

export const crowdMotionStats = { crowds: 0, packed: 0, teleports: 0 };

/** The pure bookkeeping (the harness drives it without a GPU). */
export class CrowdMotionTrack {
  readonly capacity: number;
  /** Δ per PACKED slot, xyz + w = 1 when valid (the uniform array's backing store). */
  readonly delta: Float32Array;
  private prev: Float32Array; private cur: Float32Array;
  private prevOk: Uint8Array; private curOk: Uint8Array;
  private lastSwap = -1;
  constructor(capacity: number) {
    this.capacity = capacity;
    this.delta = new Float32Array(capacity * 4);
    this.prev = new Float32Array(capacity * 3); this.cur = new Float32Array(capacity * 3);
    this.prevOk = new Uint8Array(capacity); this.curOk = new Uint8Array(capacity);
  }
  /** A new frame's staging begins (idempotent within one frame: a same-frame refresh must not age the history twice). */
  beginFrame(nowMs: number): void {
    if (this.lastSwap >= 0 && nowMs - this.lastSwap < 2) { this.curOk.fill(0); return; }
    this.lastSwap = nowMs;
    const p = this.prev; this.prev = this.cur; this.cur = p;
    const o = this.prevOk; this.prevOk = this.curOk; this.curOk = o;
    this.curOk.fill(0);
  }
  /** Logical instance i stands at ABSOLUTE (x, y, z) this frame. */
  note(i: number, x: number, y: number, z: number): void {
    if (i < 0 || i >= this.capacity) return;
    this.cur[3 * i] = x; this.cur[3 * i + 1] = y; this.cur[3 * i + 2] = z; this.curOk[i] = 1;
  }
  /** Slot v now draws logical instance i: write its Δ = P_prev − P_now. */
  pack(v: number, i: number): void {
    if (v < 0 || v >= this.capacity) return;
    let dx = 0, dy = 0, dz = 0;
    if (i >= 0 && i < this.capacity && this.prevOk[i] && this.curOk[i]) {
      dx = this.prev[3 * i] - this.cur[3 * i]; dy = this.prev[3 * i + 1] - this.cur[3 * i + 1]; dz = this.prev[3 * i + 2] - this.cur[3 * i + 2];
      if (dx * dx + dy * dy + dz * dz > TELEPORT_M * TELEPORT_M) { dx = 0; dy = 0; dz = 0; crowdMotionStats.teleports++; }
    }
    const o = 4 * v; this.delta[o] = dx; this.delta[o + 1] = dy; this.delta[o + 2] = dz; this.delta[o + 3] = 1;
  }
}

/**
 * The crowd's velocity-correct material: a CLONE of `material` (the twin's
 * master material is shared with other users — the sky-ship crew figures —
 * and must not read this crowd's Δ buffer) whose positionNode assigns
 * positionPrevious from Δ. Returns null when the backend has no velocity
 * target (the caller keeps `pinStaticMotionVectors`).
 */
export function attachCrowdMotion(material: THREE.Material, capacity: number): { material: THREE.Material; track: CrowdMotionTrack; node: { value: Float32Array; addUpdateRange?: (s: number, c: number) => void; clearUpdateRanges?: () => void } } | null {
  if (!crowdMotionSupported()) return null;
  try {
    const track = new CrowdMotionTrack(capacity);
    const deltaNode = buffer(track.delta, 'vec4', capacity);
    const mat = material.clone() as THREE.Material & { positionNode?: unknown; userData: Record<string, unknown> };
    mat.positionNode = Fn(() => {
      // positionLocal is already INSTANCED here (the positionGeometry law) — the Δ is a translation only
      positionPrevious.assign(positionLocal.add(deltaNode.element(instanceIndex).xyz));
      return positionLocal;
    })();
    mat.userData = { ...(mat.userData ?? {}), crowdMotion: true };
    mat.needsUpdate = true;
    crowdMotionStats.crowds++;
    return { material: mat, track, node: deltaNode as unknown as { value: Float32Array } };
  } catch { return null; }
}
